import { faChevronLeft, faClose } from '@fortawesome/pro-solid-svg-icons';
import { ReactNode, useState } from 'react';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { To, useLocation, useNavigate } from 'react-router-dom';
import { useInterval } from 'react-use';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { IconButton } from 'atoms/buttons/IconButton';
import { SpinningStarBall as StarBall } from 'atoms/icons/SpinningStarBall';
import { Portal } from 'atoms/layout/Portal';
import { Horizontal } from 'atoms/layout/flex';
import { HeadlineL } from 'atoms/typography';
import { useGraphqlContext } from 'contexts/graphql';
import { useIsLoggedIn } from 'hooks/auth/useIsLoggedIn';
import { useIsTabletAndAbove } from 'hooks/device/useIsTabletAndAbove';
import { useDeeplinkFromNativeApp } from 'hooks/useDeeplinkFromNativeApp';
import { useIsMobileApp } from 'hooks/useIsMobileApp';
import { glossary } from 'lib/glossary';
import { hasPreviousLocation } from 'lib/routing';

const StyledLink = styled(Link)`
  overflow: hidden;

  &:hover,
  &:focus,
  &:active {
    color: inherit;
  }
`;

const Title = styled(HeadlineL)`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
`;

const StarBallWrapper = styled.div`
  width: var(--tripple-unit);
`;

const SpinningStarBall = () => {
  const { isMobileApp } = useIsMobileApp();
  const { activeQueries } = useGraphqlContext();
  const [spinning, setSpinning] = useState(false);

  // monitor active queries to show loading spinner
  useInterval(() => {
    setSpinning(isMobileApp && activeQueries.current > 0);
  }, 100);

  return (
    <StarBallWrapper>
      <StarBall
        color="var(--c-white)"
        width={24}
        height={24}
        spinning={spinning}
      />
    </StarBallWrapper>
  );
};

type Props = {
  text?: MessageDescriptor | string;
  children?: ReactNode;
  backTo?: To;
  disableBackBrowser?: boolean;
  onClick?: () => void;
  customTitle?: ReactNode;
};

export const PageHeader = ({
  text,
  children,
  backTo,
  onClick,
  disableBackBrowser,
  customTitle,
}: Props) => {
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { isIosApp } = useIsMobileApp();
  const isLoggedIn = useIsLoggedIn();
  const isTabletAndAbove = useIsTabletAndAbove();
  const { navigateWithDeeplink } = useDeeplinkFromNativeApp();
  const location = useLocation();

  if (!isLoggedIn) return null;

  // back browser can be disabled using prop or
  // is always disabled if user comes from global NavItem
  const shouldDisableBackBrowser =
    disableBackBrowser || location.state?.fromNavItem;

  return (
    <Portal id="page-header-title">
      {children || (
        <Horizontal>
          {backTo ||
          (!shouldDisableBackBrowser && hasPreviousLocation(-1)) ||
          onClick ? (
            <IconButton
              color="transparent"
              onClick={() => {
                if (onClick) {
                  onClick();
                } else if (backTo) {
                  navigate(backTo);
                } else {
                  navigate(-1);
                }
              }}
              icon={faChevronLeft}
              aria-label={formatMessage(glossary.back)}
            />
          ) : (
            <>
              {!isTabletAndAbove && !isIosApp && <SpinningStarBall />}
              {isIosApp && (
                <IconButton
                  color="transparent"
                  onClick={() => {
                    navigateWithDeeplink();
                  }}
                  icon={faClose}
                  aria-label={formatMessage(glossary.close)}
                />
              )}
            </>
          )}
          {text && (
            <StyledLink
              to="."
              onClick={e => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <Title as="p">
                {typeof text === 'string' ? (
                  text
                ) : (
                  <FormattedMessage {...text} />
                )}
              </Title>
            </StyledLink>
          )}
          {customTitle}
        </Horizontal>
      )}
    </Portal>
  );
};
